import { SUPPORT_EMAIL } from "../constants";

/**
 * ENG-3413 — what a failed sign-in, sign-up or password change says, by Firebase error code.
 *
 * Kept out of `useAuth` so the three sign-in popups say the same thing, and so each message is
 * assertable without a render. Three of the old messages were wrong for some readers:
 *
 * - `auth/too-many-requests` and `auth/user-disabled` fell through to "An unexpected error
 *   occurred", which names neither the cause nor the way out.
 * - `auth/account-exists-with-different-credential` always said "Try signing in with email/password",
 *   also when the account it found signs in with Google. Firebase does not say which method the
 *   existing account uses (with email enumeration protection on, nothing will), so the message names
 *   none.
 * - `auth/invalid-credential` is what Firebase returns for a wrong password AND for an address with no
 *   account, since email enumeration protection merges the two. It read "Incorrect password", which
 *   sends someone who mistyped their email to retype a password that was right.
 */

const NETWORK = "Network error. Please check your connection and try again.";
const TOO_MANY = "Too many attempts. Wait a few minutes, then try again.";
const DISABLED = `This account has been disabled. Email ${SUPPORT_EMAIL} for help.`;

/**
 * ENG-3411: popup sign-in needs this site to be able to save data. Firebase checks for it in a frame
 * on the sign-in domain once the popup is open, and where the browser blocks site data it fails with
 * `auth/web-storage-unsupported`. Firebase's own text for that blames third-party cookies, which is
 * not what is blocked here, so it gets its own message. Email and password still work where site
 * data is blocked; Firebase keeps that session in memory, so it lasts for this visit only.
 */
const siteDataBlocked = (provider: string) =>
  `Your browser is blocking this site from saving data, and ${provider} sign-in needs it. Allow this site to save data in your browser settings, then try again.`;

/**
 * The Google, Microsoft and Apple popups. `fallback` is what an unlisted code says, as before, and
 * `provider` names the popup in the messages that need it.
 *
 * ENG-3625: `resolverWasReady` is whether the popup sign-in iframe had loaded when the button was
 * pressed (lib/firebase.ts). If it had not, the window opened only after it loaded, outside the press,
 * which Safari and iOS block. It has loaded by the time this error arrives, so a second press works,
 * and "allow popups" would send someone to their settings for nothing. `button` is the label of the
 * button that opened it, which that message names: "Continue with <provider>" unless given.
 */
export const popupSignInError = (
  code: string,
  fallback: string,
  provider: string,
  resolverWasReady = true,
  button = `Continue with ${provider}`,
): string => {
  switch (code) {
    case "auth/popup-blocked":
      return resolverWasReady
        ? "Please allow popups for this site and try again."
        : `${provider} sign-in was still loading, so your browser blocked its window. Press ${button} again.`;
    case "auth/web-storage-unsupported":
      return siteDataBlocked(provider);
    case "auth/popup-closed-by-user":
    case "auth/cancelled-popup-request":
      return "Sign-in was cancelled. Please try again.";
    case "auth/network-request-failed":
      return NETWORK;
    case "auth/account-exists-with-different-credential":
      return "This email already has a Haven account that signs in a different way. Sign in the way you did when you created it.";
    case "auth/too-many-requests":
      return TOO_MANY;
    case "auth/user-disabled":
      return DISABLED;
    default:
      return fallback;
  }
};

/** The email and password form, signing in or creating an account. */
export const emailAuthError = (code: string, isSignUp: boolean): string => {
  switch (code) {
    case "auth/invalid-email":
      return "Invalid email format. Please check and try again.";
    case "auth/email-already-in-use":
      // The step that creates an account has no Google button, so this cannot point to one.
      return "This email is already registered. Sign in instead, with Google if that's how you signed up.";
    case "auth/user-not-found":
      return "No account found with this email. Please check your email or sign up.";
    case "auth/wrong-password":
      return "Incorrect password. If you created your account with Google, use Google sign-in instead.";
    case "auth/invalid-credential":
      return "Incorrect email or password. Check both and try again. If you created your account with Google, use Google sign-in instead.";
    case "auth/weak-password":
      return "Password is too weak. Use at least 6 characters.";
    case "auth/too-many-requests":
      // Firebase lifts the block when the password is reset, so signing in has a way out to offer.
      return isSignUp ? TOO_MANY : "Too many attempts. Wait a few minutes, then try again, or reset your password.";
    case "auth/user-disabled":
      return DISABLED;
    case "auth/network-request-failed":
      return NETWORK;
    default:
      return "An unexpected error occurred. Please try again later.";
  }
};

/** Settings' Change Password, which re-checks the current password first. */
export const changePasswordError = (code: string): string => {
  switch (code) {
    case "auth/wrong-password":
    case "auth/invalid-credential":
      // The email is the signed-in account's, so here the credential can only be the password.
      return "Current password is incorrect.";
    case "auth/weak-password":
      return "New password is too weak. Use at least 6 characters.";
    case "auth/too-many-requests":
      return "Too many attempts. Wait a few minutes, then try again, or reset your password by email.";
    case "auth/user-disabled":
      return DISABLED;
    case "auth/network-request-failed":
      return NETWORK;
    default:
      // This used to be Firebase's own text, e.g. "Firebase: Error (auth/internal-error)."
      return "Couldn't change your password. Please try again.";
  }
};
